import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useGroups, useGroupProjects, useProjectByKey } from '../lib/queries'
import { useCurrentUser } from '../lib/auth'
import { apiGet, apiPost } from '../lib/api'
import type { ProjectDto } from '../lib/types'

interface TopNavProps {
  /** 명시적 오버라이드 — 보통은 currentProjectKey 만으로 자동 추론 */
  currentGroupUuid?: string
  currentProjectKey?: string
}

export function TopNav({ currentGroupUuid, currentProjectKey }: TopNavProps) {
  const navigate = useNavigate()
  const me = useCurrentUser()
  const groups = useGroups()
  const project = useProjectByKey(currentProjectKey)

  // 그룹 셀렉터의 selected: 명시 props > project.groupId 로부터 추론
  const derivedGroupUuid = useMemo(() => {
    if (currentGroupUuid) return currentGroupUuid
    if (project.data && groups.data) {
      return groups.data.find((g) => g.id === project.data.groupId)?.uuid
    }
    return undefined
  }, [currentGroupUuid, project.data, groups.data])

  const projects = useGroupProjects(derivedGroupUuid)

  const onGroupChange = async (uuid: string) => {
    if (!uuid) { navigate('/groups'); return }
    try {
      const list = await apiGet<ProjectDto[]>(`/api/groups/${uuid}/projects`)
      const first = list[0]
      if (first) navigate(`/projects/${encodeURIComponent(first.projectKey)}`)
      else navigate('/groups')
    } catch { navigate('/groups') }
  }

  const onLogout = async () => {
    try { await apiPost('/api/auth/logout', {}) } catch { /* ignore */ }
    navigate('/login')
  }

  return (
    <nav>
      <div className="nav-container">
        <Link to="/groups" className="nav-brand">
          <img src="/images/logo-icon.png" alt="" className="nav-logo" />
          <span className="nav-brand-text">AI-README</span>
        </Link>
        <div className="nav-switchers loaded">
          <div className="group-switcher">
            <label htmlFor="nav-group-select" className="muted">Group</label>
            <select
              id="nav-group-select"
              value={derivedGroupUuid ?? ''}
              disabled={!groups.data || groups.data.length === 0}
              onChange={(e) => onGroupChange(e.target.value)}
            >
              {!derivedGroupUuid && <option value="">Select group</option>}
              {(!groups.data || groups.data.length === 0) && <option value="">No groups</option>}
              {groups.data?.map((g) => (
                <option key={g.uuid} value={g.uuid}>{g.name}</option>
              ))}
            </select>
          </div>
          <div className="project-switcher">
            <label htmlFor="nav-project-select" className="muted">Project</label>
            <select
              id="nav-project-select"
              value={currentProjectKey ?? ''}
              disabled={!projects.data || projects.data.length === 0}
              onChange={(e) => {
                const key = e.target.value
                if (key) navigate(`/projects/${encodeURIComponent(key)}`)
              }}
            >
              {!currentProjectKey && <option value="">Select project</option>}
              {(!projects.data || projects.data.length === 0) && <option value="">No project</option>}
              {projects.data?.map((p) => (
                <option key={p.projectKey} value={p.projectKey}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="nav-right">
          <Link to="/trash" className="nav-trash">Trash</Link>
          {me.data && (
            <div className="nav-user" id="nav-user">
              <span>{me.data.email}</span>
              <button type="button" id="logout-btn" className="btn btn-ghost btn-sm" onClick={onLogout}>Log out</button>
            </div>
          )}
        </div>
      </div>
    </nav>
  )
}
